import { Button, InputText, Text } from '@msc-b2c/shared-components';
import styles from './AlreadyMemberLayout.module.scss';
import { VoyagersClubAlreadyAMemberProps } from '@msc-b2c/interfaces/types';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  closeLoader,
  getActiveSession,
  getKeyboardType,
  GetVoyagerClubInfoMyMSC,
  membershipDataIsLoading,
  membershipsData,
  openLoader,
  setErrorManagerCode,
  setMembershipJustLinked,
  UpdateVoyagerClubInfoMyMsc,
  validateFormField,
} from '@msc-b2c/shared-utils';
import {
  RetrieveVoyagerClubInfoMyMSCResponse,
  UpdateVoyagerClubInfoMyMSCResponse,
} from '@msc-b2c/interfaces/interfaces';
import { useDispatch } from 'react-redux';

export function AlreadyMemberLayout(props: Readonly<VoyagersClubAlreadyAMemberProps>) {
  const dispatch = useDispatch();

  const {
    alreadyAMemberTitle,
    alreadyAMemberDescription,
    alreadyAMemberInputLabel,
    alreadyAMemberButtonLabel,
    validationRulesContainerVoyagerNumber,
    alreadyAMemberButtonStyle,
    alreadyAMemberButtonSize,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const { email } = getActiveSession();

  type FormShape = { membership: { number: string } };
  const {
    register,
    formState: { errors, isValid },
    clearErrors,
    trigger,
    handleSubmit,
  } = useForm<FormShape>({
    shouldFocusError: false,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { membership: { number: '' } },
  });

  const isSubmitDisabled = isLoading || !isValid;

  const refetchMembershipData = async () => {
    dispatch(membershipDataIsLoading(true));
    try {
      const voyagerClubInfoAPI = new GetVoyagerClubInfoMyMSC(false, { email: email ?? '' });
      const data: RetrieveVoyagerClubInfoMyMSCResponse = await voyagerClubInfoAPI.getData();
      if (data?.memberships) {
        dispatch(membershipsData(data));
        dispatch(setMembershipJustLinked(true));
      }
    } catch (error) {
      dispatch(
        setErrorManagerCode((error as { issue_code?: string })?.issue_code ?? 'unknown_error')
      );
    } finally {
      dispatch(membershipDataIsLoading(false));
    }
  };

  const onSubmitClick = handleSubmit((data) => {
    const number = (data.membership?.number ?? '').toString().trim();

    setIsLoading(true);
    openLoader('.loader');
    const api = new UpdateVoyagerClubInfoMyMsc(false, false, { number }, { email: email ?? '' });
    api
      .sendData()
      .then(async (res: UpdateVoyagerClubInfoMyMSCResponse) => {
        clearErrors('membership.number');
        if (res?.number) {
          await refetchMembershipData();
        }
      })
      .catch((error) => {
        dispatch(setErrorManagerCode(error?.issue_code ?? 'unknown_error'));
      })
      .finally(() => {
        setIsLoading(false);
        closeLoader('.loader');
      });
  });

  return (
    <div className={styles['cmp-already-member']}>
      <div className={styles['cmp-already-member__container']}>
        <div className={styles['cmp-already-member__header']}>
          <Text
            text={alreadyAMemberTitle ?? ''}
            styleOption={`${styles['cmp-already-member__header__title']} header-m`}
          />
        </div>

        {alreadyAMemberDescription && (
          <div className={styles['cmp-already-member__body']}>
            <Text
              text={alreadyAMemberDescription}
              styleOption={styles['cmp-already-member__text']}
            />
          </div>
        )}

        <form
          className={styles['cmp-already-member__form']}
          onSubmit={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        >
          <InputText
            index={2}
            inputType={getKeyboardType(validationRulesContainerVoyagerNumber)}
            name={'membership.number'}
            label=""
            placeholder={alreadyAMemberInputLabel ?? ''}
            register={register}
            errors={errors}
            styleOptions={styles['cmp-already-member__form__input']}
            clearErrors={clearErrors}
            description={
              validationRulesContainerVoyagerNumber?.find(
                (rule) => rule.validationRules !== 'vr-mandatory-field'
              )?.validationRuleErrorMessage
            }
            required={validationRulesContainerVoyagerNumber?.some(
              (rule) => rule.validationRules === 'vr-mandatory-field'
            )}
            clearErrorsOnFocus={false}
            validation={{
              validate: (value) =>
                validateFormField(value, validationRulesContainerVoyagerNumber || []),
              onBlur: () => trigger('membership.number'),
            }}
          />
          <Button
            text={alreadyAMemberButtonLabel ?? ''}
            onClick={() => {
              onSubmitClick();
            }}
            styleOption={`${alreadyAMemberButtonStyle} ${alreadyAMemberButtonSize}`}
            disabled={isSubmitDisabled}
          />
        </form>
      </div>
    </div>
  );
}

export default AlreadyMemberLayout;
